import { ReceivingStatus } from '../../../models/poData/ReceivingStatus';
import { PoLineItem } from '../../../models/poData/PoLineItem';

/**
 * How much the received-vs-ordered math on a PO can be trusted, assessed from
 * the PO vs. its logs vs. its receipts:
 *
 * - `fresh`    — nothing ever received (no logs, no receipts). The app can
 *                confidently assume what's missing purely from this check-off.
 * - `ledgered` — prior deliveries exist and every one is accounted for with
 *                structured receipts. Missing = expected − ledger − this
 *                check-off; the math is trustworthy.
 * - `legacy`   — the PO has pre-feature logs without receipts
 *                (`receivingStatus.legacyLogs`). The ledger understates what's
 *                really in, so missing amounts are a starting guess the user
 *                must reconcile by hand.
 *
 * Every scenario routes through the same two pages (what arrived → what's
 * still missing); the scenario decides how confidently the second page
 * pre-fills and how it's worded.
 */
export type ReceivingScenario = 'fresh' | 'ledgered' | 'legacy';

/**
 * Classifies a PO into its receiving scenario off the PO data read.
 *
 * Any legacy logs → `legacy` (a PO can be hybrid — old logs plus new-ledger
 * rows — and its math still stays incomplete). Otherwise, any ledger activity
 * (received quantity, a closed line, or an off-PO receipt) → `ledgered`;
 * none → `fresh`. `receivingStatus === null` (the PO-level status sub-read
 * soft-failed) can't rule out legacy logs, so it falls back to the per-line
 * data alone — treat the result cautiously alongside `errorMessages`.
 *
 * @param receivingStatus - The PO-level receiving block, or `null` on soft-fail.
 * @param lineItems - The PO's line items, which carry the per-line rollup.
 * @returns The scenario driving the missing-page prefill confidence and wording.
 */
export function classifyReceivingScenario(
  receivingStatus: ReceivingStatus | null,
  lineItems: PoLineItem[],
): ReceivingScenario {
  if (receivingStatus && receivingStatus.legacyLogs.length > 0) {
    return 'legacy';
  }

  return hasTrackedReceipts(receivingStatus, lineItems) ? 'ledgered' : 'fresh';
}

/**
 * Whether the PO has any tracked (structured-ledger) receipts — a received
 * quantity, a ledger-closed line, an off-PO receipt, or any line-level receipt
 * row (a ragged line's parts show only there, with its aggregate `qtyReceived`
 * stuck at 0). This is the only received data with line-level visibility;
 * legacy logs are opaque free-text with no per-line breakdown, so they never
 * count here.
 *
 * @param receivingStatus - The PO-level receiving block, or `null` on soft-fail.
 * @param lineItems - The PO's line items, which carry the per-line rollup.
 * @returns True when at least one tracked receipt exists on the PO.
 */
export function hasTrackedReceipts(
  receivingStatus: ReceivingStatus | null,
  lineItems: PoLineItem[],
): boolean {
  return (
    (receivingStatus?.offPoReceipts?.length ?? 0) > 0 ||
    (lineItems || []).some(
      (li) => (li.qtyReceived ?? 0) > 0 || !!li.isClosed || (li.receipts?.length ?? 0) > 0,
    )
  );
}

/**
 * Which "heads up" the receiver should see about how trustworthy the on-screen
 * received data is:
 *
 * - `none`       — no legacy logs. Received counts are complete and reliable
 *                  (fresh or fully-ledgered PO); nothing to warn about.
 * - `legacyOnly` — legacy logs but no tracked receipts. Nothing shows as
 *                  received even though earlier deliveries happened; the ledger
 *                  simply can't see them.
 * - `mixed`      — legacy logs AND tracked receipts. The counts reflect only
 *                  the tracked receipts, so some lines may already be received
 *                  (via a legacy delivery) without showing it. This is the case
 *                  where graying is asymmetric: a grayed line is provably
 *                  received, but an un-grayed line is NOT provably outstanding.
 *
 * A refinement of the `legacy` scenario (the two non-`none` kinds only occur
 * when `classifyReceivingScenario` returns `legacy`), split out because the
 * banner wording differs and the mixed case needs the pill relabel.
 *
 * @param receivingStatus - The PO-level receiving block, or `null` on soft-fail.
 * @param lineItems - The PO's line items, which carry the per-line rollup.
 * @returns The notice kind driving the receiving banner + closed-pill label.
 */
export function receivingNotice(
  receivingStatus: ReceivingStatus | null,
  lineItems: PoLineItem[],
): ReceivingNoticeKind {
  if (classifyReceivingScenario(receivingStatus, lineItems) !== 'legacy') {
    return 'none';
  }
  return hasTrackedReceipts(receivingStatus, lineItems) ? 'mixed' : 'legacyOnly';
}

export type ReceivingNoticeKind = 'none' | 'legacyOnly' | 'mixed';
